import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Box,
  Typography,
  styled,
  FormControlLabel,
  Checkbox,
  Link,
} from '@material-ui/core';
import { useFormContext, Controller } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { useParams, useLocation } from 'react-router-dom';
import { isValidPhoneNumber } from 'react-phone-number-input';

import { SoleraAccountCreationParams } from '../SoleraAccountCreationPage';
import { SoleraAccountCreationStep } from '../soleraAccountCreationState';

import sparkTheme from '@/themes/sparkTheme';
import { Button, TextField } from '@/components';
import { ENROLL_SOLERA_PATIENT, SEND_EMAIL_CHANGE_VERIFICATION } from '@/apollo/mutations';
import { isSpark as isSparkHelper, getPdtUid } from '@/utils/productHelpers';
import { useUserUid } from '@/hooks/useUserUid';
import { PhoneNumberInput } from '@/pages/Onboarding/components/PhoneNumberInput/PhoneNumberInput';

const ContentContainer = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  marginTop: 8,
  gap: 24,
});

const LOOKUP_KEY_PATTERN = /^[A-Za-z0-9-]{24,36}$/;

type Props = {
  onNext: (step: SoleraAccountCreationStep, data?: { careTeamUid: string; token?: string }) => void;
};

const ConfirmAccountInfo = ({ onNext }: Props) => {
  const { t } = useTranslation();
  const {
    product,
  } = useParams<SoleraAccountCreationParams>();
  const location = useLocation();
  const searchParams = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const lookupKeyParam = searchParams.get('lookupKey')?.trim() || '';

  // Hardcoded organization slug for Solera
  const SOLERA_ORGANIZATION_SLUG = 'solera';

  const { setUserUid } = useUserUid();
  const isSpark = isSparkHelper(product);

  const formContext = useFormContext();
  const {
    formState: { errors, isValid }, clearErrors, getValues, setValue,
  } = formContext;

  const [savingData, setSavingData] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [enrollmentId, setEnrollmentId] = useState<string | null>(null);
  const [storedCareTeamUid, setStoredCareTeamUid] = useState<string | null>(null);
  const [storedToken, setStoredToken] = useState<string | null>(null);
  const [originalEmail, setOriginalEmail] = useState<string | null>(null);

  const [enrollSoleraPatient, { loading: enrollLoading }] = useMutation(ENROLL_SOLERA_PATIENT);
  const [sendEmailChangeVerification] = useMutation(SEND_EMAIL_CHANGE_VERIFICATION);

  useEffect(() => {
    if (!lookupKeyParam) {
      setErrorMessage(t('solera.account.errors.lookup_missing'));
      setEnrollmentId(null);
      return;
    }

    if (!LOOKUP_KEY_PATTERN.test(lookupKeyParam)) {
      setErrorMessage(t('solera.account.errors.lookup_invalid'));
      setEnrollmentId(null);
      return;
    }

    setEnrollmentId(lookupKeyParam);
    setErrorMessage(null);
  }, [lookupKeyParam, t]);

  useEffect(() => {
    if (!enrollmentId) {
      return;
    }

    const fetchSoleraEnrollment = async () => {
      try {
        setErrorMessage(null);

        const response = await enrollSoleraPatient({
          variables: {
            enrollmentId,
            pdtUid: getPdtUid(product),
            organizationSlug: SOLERA_ORGANIZATION_SLUG,
          },
        });

        const result = response.data?.enrollSoleraPatient;
        if (!result?.success || !result.patient || !result.careTeamUid) {
          setErrorMessage(t('solera.account.errors.enrollment_not_found'));
          return;
        }

        const { patient, careTeamUid: returnedCareTeamUid, token: enrollmentToken } = result;
        setStoredCareTeamUid(returnedCareTeamUid);
        if (enrollmentToken) {
          setStoredToken(enrollmentToken);
        }
        if (patient.user?.uid) {
          setUserUid(patient.user.uid);
        }

        const emailFromResponse = patient.user?.email || '';
        const phoneFromResponse = patient.user?.phone || '';

        if (emailFromResponse) {
          setOriginalEmail(emailFromResponse);
          setValue('email', emailFromResponse, { shouldValidate: true, shouldDirty: false });
        }

        if (phoneFromResponse) {
          setValue('phoneNumber', phoneFromResponse, { shouldValidate: true, shouldDirty: false });
        }
      } catch (error) {
        setErrorMessage(t('solera.account.errors.enrollment_generic'));
      }
    };

    fetchSoleraEnrollment();
  }, [enrollmentId, enrollSoleraPatient, setUserUid, setValue, t, product]);

  // Save Solera data via enrollSoleraPatient

  const handleSubmit = useCallback(async () => {
    if (errorMessage || enrollLoading || !storedCareTeamUid) {
      return;
    }

    setSavingData(true);

    try {
      const currentEmail = getValues('email');
      const emailChanged = originalEmail && currentEmail !== originalEmail;

      if (emailChanged && currentEmail && originalEmail) {
        if (!storedToken) {
          setErrorMessage(t(
            'solera.account.errors.token_missing',
            'Authentication token is missing. Please try again.',
          ));
          setSavingData(false);
          return;
        }

        // Send verification email for the new email
        const response = await sendEmailChangeVerification({
          variables: {
            previousEmail: originalEmail,
            email: currentEmail,
            pdtUid: getPdtUid(product),
            token: storedToken,
          },
        });

        if (response.data?.sendEmailChangeVerification?.success) {
          // Store careTeamUid, email, and token in localStorage in case user is redirected back
          if (storedCareTeamUid) {
            localStorage.setItem('solera_careTeamUid', storedCareTeamUid);
          }
          if (currentEmail) {
            localStorage.setItem('solera_email', currentEmail);
          }
          if (originalEmail) {
            localStorage.setItem('solera_originalEmail', originalEmail);
          }
          if (storedToken) {
            localStorage.setItem('solera_token', storedToken);
          }
          onNext('EmailVerification', {
            careTeamUid: storedCareTeamUid,
            token: storedToken || undefined,
          });
        } else {
          setErrorMessage(t('solera.account.errors.email_verification_failed'));
        }
      } else {
        // Email not changed, go directly to password creation
        onNext('CreatePassword', {
          careTeamUid: storedCareTeamUid,
          token: storedToken || undefined,
        });
      }
    } catch (error) {
      console.error('Error in form submission:', error);
      setErrorMessage(t('solera.account.errors.submission_error'));
    } finally {
      setSavingData(false);
    }
  }, [
    errorMessage,
    enrollLoading,
    onNext,
    storedCareTeamUid,
    originalEmail,
    getValues,
    sendEmailChangeVerification,
    product,
    t,
    storedToken,
  ]);

  return (
    <ContentContainer>
      <Box>
        <Typography variant="h3" style={{ fontWeight: 700 }}>
          {t('solera.account.welcome_title')}
        </Typography>
        <Typography color="textSecondary" style={{ marginTop: 12 }}>
          {t('solera.account.welcome_subtitle')}
        </Typography>
        {errorMessage && (
          <Typography color="error" style={{ marginTop: 16 }}>
            {errorMessage}
          </Typography>
        )}
      </Box>
      <Controller
        name="email"
        render={({ field }) => (
          <TextField
            {...field}
            data-testid="email-input"
            required
            label={t('solera.account.email_label', 'Email')}
            placeholder={t('solera.account.email_placeholder', 'you@example.com')}
            helperText={errors?.email?.message}
            error={!!errors?.email}
            onFocus={() => clearErrors('email')}
            InputLabelProps={{
              shrink: true,
            }}
          />
        )}
      />
      <Controller
        name="phoneNumber"
        control={formContext.control}
        rules={{
          required: t('coverage.sleepio.patient.please_enter_valid_phone_number'),
          validate: (v) => isValidPhoneNumber(v || '')
            || t('coverage.sleepio.patient.please_enter_valid_phone_number'),
        }}
        render={({ field, fieldState }) => (
          <PhoneNumberInput
            id="phoneNumber"
            value={field.value ?? ''}
            onChange={field.onChange}
            onBlur={field.onBlur}
            placeholder={t('solera.account.phone_placeholder', 'Phone number')}
            error={!!fieldState.error}
            helperText={
              fieldState.error
                ? t('coverage.sleepio.patient.please_enter_valid_phone_number')
                : undefined
            }
            required
          />
        )}
      />
      <Controller
        name="agreeToReceiveTextMessages"
        control={formContext.control}
        rules={{ required: true }}
        render={({ field }) => (
          <FormControlLabel
            style={{ marginTop: 8, alignItems: 'center' }}
            label={(
              <Typography>
                {t('solera.account.sms_consent')}
              </Typography>
            )}
            control={<Checkbox {...field} checked={!!field.value} />}
          />
        )}
      />
      <Controller
        name="termsAndPrivacy"
        control={formContext.control}
        rules={{ required: true }}
        render={({ field }) => (
          <FormControlLabel
            style={{ marginTop: 4, marginBottom: 16, alignItems: 'center' }}
            label={(
              <Typography>
                {t('solera.account.terms_prefix')}
                {' '}
                <Link
                  target="_blank"
                  rel="noopener noreferrer"
                  href={t('footer.terms_of_use.link')}
                  underline="always"
                  color="textPrimary"
                >
                  {t('footer.terms_of_use.label')}
                </Link>
                {' '}
                {t('solera.account.terms_middle')}
                {' '}
                <Link
                  target="_blank"
                  rel="noopener noreferrer"
                  href={t('footer.privacy_notice.link')}
                  underline="always"
                  color="textPrimary"
                >
                  {t('footer.privacy_notice.label')}
                </Link>
                .
              </Typography>
            )}
            control={<Checkbox {...field} checked={!!field.value} />}
          />
        )}
      />
      {errors?.termsAndPrivacy?.message && (
        <Typography color="error" variant="body2" style={{ marginTop: -8 }}>
          {String(errors.termsAndPrivacy.message)}
        </Typography>
      )}
      <Button
        role="button"
        variant="contained"
        data-testid="submit-button"
        type="button"
        fullWidth
        color="primary"
        disabled={!isValid || savingData || enrollLoading}
        rounded
        loading={savingData || enrollLoading}
        style={
          isSpark
            ? {
              borderRadius: 100,
              ...(isValid && !savingData && !enrollLoading && !errorMessage
                ? {
                  backgroundColor: sparkTheme.palette.primary.main,
                  color: sparkTheme.palette.getContrastText(
                    sparkTheme.palette.primary.main,
                  ),
                }
                : {}),
            }
            : undefined
        }
        onClick={handleSubmit}
      >
        {t('solera.account.submit')}
      </Button>
    </ContentContainer>
  );
};

export default ConfirmAccountInfo;
